import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronDownIcon, ChevronUpIcon, MapPinIcon, DollarSignIcon, CalendarIcon, BarChart2Icon, CheckIcon, XCircleIcon } from 'lucide-react';
type Project = {
  id: number;
  title: string;
  location: string;
  image: string;
  description: string;
  funding: string;
  return: string;
  timeline: string;
  match: number;
  tags: string[];
  type: string;
};
type ProjectComparisonToolProps = {
  projects: Project[];
  onClose: () => void;
};
export const ProjectComparisonTool: React.FC<ProjectComparisonToolProps> = ({
  projects,
  onClose
}) => {
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['basic', 'financial']);
  // Pre-select first two projects for comparison by default
  useEffect(() => {
    if (projects.length >= 2) {
      setSelectedProjects([projects[0].id, projects[1].id]);
    } else if (projects.length === 1) {
      setSelectedProjects([projects[0].id]);
    }
  }, [projects]);
  // Toggle project selection
  const toggleProjectSelection = (projectId: number) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } else {
      // Limit to 3 projects for comparison
      if (selectedProjects.length < 3) {
        setSelectedProjects([...selectedProjects, projectId]);
      }
    }
  };
  // Toggle section expansion
  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };
  // Get selected project data
  const selectedProjectsData = projects.filter(project => selectedProjects.includes(project.id));
  // Check if a section is expanded
  const isSectionExpanded = (section: string) => {
    return expandedSections.includes(section);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-156">
      <motion.div initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.9,
      opacity: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 300
    }} className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-157">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10" data-id="element-158">
          <h2 className="text-xl font-semibold text-soil-brown" data-id="element-159">
            Compare Projects
          </h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" aria-label="Close comparison" data-id="element-160">
            <XIcon size={24} data-id="element-161" />
          </button>
        </div>
        {/* Project Selection */}
        <div className="p-4 border-b" data-id="element-162">
          <div className="relative" data-id="element-163">
            <button onClick={() => setShowDropdown(!showDropdown)} className="btn btn-outline w-full flex items-center justify-between" data-id="element-164">
              <span data-id="element-165">
                {selectedProjects.length === 0 ? 'Select projects to compare' : `Comparing ${selectedProjects.length} project${selectedProjects.length > 1 ? 's' : ''}`}
              </span>
              <ChevronDownIcon size={20} data-id="element-166" />
            </button>
            {/* Dropdown */}
            <AnimatePresence data-id="element-167">
              {showDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-20 max-h-60 overflow-y-auto" data-id="element-168">
                  {projects.map(project => <div key={project.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer" onClick={() => toggleProjectSelection(project.id)} data-id="element-169">
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center mr-3 ${selectedProjects.includes(project.id) ? 'bg-leaf-green border-leaf-green' : 'border-gray-300'}`} data-id="element-170">
                        {selectedProjects.includes(project.id) && <CheckIcon size={14} className="text-white" data-id="element-171" />}
                      </div>
                      <div className="flex items-center" data-id="element-172">
                        <img src={project.image} alt={project.title} className="w-10 h-10 object-cover rounded-md mr-3" data-id="element-173" />
                        <div data-id="element-174">
                          <p className="font-medium text-sm" data-id="element-175">{project.title}</p>
                          <div className="flex items-center text-xs text-gray-500" data-id="element-176">
                            <MapPinIcon size={10} className="mr-1" data-id="element-177" />
                            <span data-id="element-178">{project.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </motion.div>}
            </AnimatePresence>
          </div>
          {/* Selected Projects Preview */}
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-179">
            {selectedProjectsData.map(project => <div key={project.id} className="bg-gray-50 rounded-md p-2 flex items-center" data-id="element-180">
                <img src={project.image} alt={project.title} className="w-8 h-8 object-cover rounded-md mr-2" data-id="element-181" />
                <span className="text-sm font-medium mr-2" data-id="element-182">
                  {project.title}
                </span>
                <button onClick={() => toggleProjectSelection(project.id)} className="text-gray-500 hover:text-red-500" aria-label={`Remove ${project.title} from comparison`} data-id="element-183">
                  <XIcon size={16} data-id="element-184" />
                </button>
              </div>)}
          </div>
        </div>
        {/* Comparison Content */}
        <div className="overflow-y-auto" style={{
        maxHeight: 'calc(90vh - 160px)'
      }} data-id="element-185">
          {selectedProjectsData.length > 0 ? <div className="p-4" data-id="element-186">
              {/* Basic Info Section */}
              <div className="mb-4" data-id="element-187">
                <div className="flex items-center justify-between cursor-pointer mb-2" onClick={() => toggleSection('basic')} data-id="element-188">
                  <h3 className="font-semibold" data-id="element-189">Basic Information</h3>
                  {isSectionExpanded('basic') ? <ChevronUpIcon size={20} data-id="element-190" /> : <ChevronDownIcon size={20} data-id="element-191" />}
                </div>
                <AnimatePresence data-id="element-192">
                  {isSectionExpanded('basic') && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-193">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-194">
                        {selectedProjectsData.map(project => <div key={project.id} className="border rounded-lg overflow-hidden" data-id="element-195">
                            <div className="h-40 overflow-hidden" data-id="element-196">
                              <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-197" />
                            </div>
                            <div className="p-3" data-id="element-198">
                              <h4 className="font-semibold text-lg mb-1" data-id="element-199">
                                {project.title}
                              </h4>
                              <div className="flex items-center text-gray-600 mb-2" data-id="element-200">
                                <MapPinIcon size={14} className="mr-1" data-id="element-201" />
                                <span className="text-sm" data-id="element-202">
                                  {project.location}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2 line-clamp-3" data-id="element-203">
                                {project.description}
                              </p>
                              <div className="flex flex-wrap gap-1 mt-2" data-id="element-204">
                                {project.tags.slice(0, 3).map((tag, index) => <span key={index} className="bg-wheat-gold/50 text-soil-brown text-xs px-2 py-0.5 rounded-full" data-id="element-205">
                                    {tag}
                                  </span>)}
                                {project.tags.length > 3 && <span className="text-xs text-gray-500" data-id="element-206">
                                    +{project.tags.length - 3} more
                                  </span>}
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
              {/* Financial Section */}
              <div className="mb-4" data-id="element-207">
                <div className="flex items-center justify-between cursor-pointer mb-2" onClick={() => toggleSection('financial')} data-id="element-208">
                  <h3 className="font-semibold" data-id="element-209">Financial Details</h3>
                  {isSectionExpanded('financial') ? <ChevronUpIcon size={20} data-id="element-210" /> : <ChevronDownIcon size={20} data-id="element-211" />}
                </div>
                <AnimatePresence data-id="element-212">
                  {isSectionExpanded('financial') && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-213">
                      <div className="overflow-x-auto" data-id="element-214">
                        <table className="w-full border-collapse" data-id="element-215">
                          <thead data-id="element-216">
                            <tr className="bg-gray-50" data-id="element-217">
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-218">
                                Project
                              </th>
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-219">
                                Funding Required
                              </th>
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-220">
                                Expected Return
                              </th>
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-221">
                                Timeline
                              </th>
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-222">
                                Match Score
                              </th>
                            </tr>
                          </thead>
                          <tbody data-id="element-223">
                            {selectedProjectsData.map(project => <tr key={project.id} className="hover:bg-gray-50" data-id="element-224">
                                <td className="p-3 border" data-id="element-225">
                                  <div className="font-medium" data-id="element-226">
                                    {project.title}
                                  </div>
                                </td>
                                <td className="p-3 border" data-id="element-227">
                                  <div className="flex items-center" data-id="element-228">
                                    <DollarSignIcon size={14} className="mr-1 text-soil-brown" data-id="element-229" />
                                    <span data-id="element-230">{project.funding}</span>
                                  </div>
                                </td>
                                <td className="p-3 border" data-id="element-231">{project.return}</td>
                                <td className="p-3 border" data-id="element-232">
                                  <div className="flex items-center" data-id="element-233">
                                    <CalendarIcon size={14} className="mr-1 text-soil-brown" data-id="element-234" />
                                    <span data-id="element-235">{project.timeline}</span>
                                  </div>
                                </td>
                                <td className="p-3 border" data-id="element-236">
                                  <div className="flex items-center" data-id="element-237">
                                    <div className="w-full bg-gray-200 rounded-full h-2 mr-2 max-w-24" data-id="element-238">
                                      <div className="bg-leaf-green h-2 rounded-full" style={{
                                width: `${project.match}%`
                              }} data-id="element-239"></div>
                                    </div>
                                    <span className="font-medium text-sm text-leaf-green" data-id="element-240">
                                      {project.match}%
                                    </span>
                                  </div>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
              {/* Features Comparison */}
              <div className="mb-4" data-id="element-241">
                <div className="flex items-center justify-between cursor-pointer mb-2" onClick={() => toggleSection('features')} data-id="element-242">
                  <h3 className="font-semibold" data-id="element-243">Features Comparison</h3>
                  {isSectionExpanded('features') ? <ChevronUpIcon size={20} data-id="element-244" /> : <ChevronDownIcon size={20} data-id="element-245" />}
                </div>
                <AnimatePresence data-id="element-246">
                  {isSectionExpanded('features') && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-247">
                      <div className="overflow-x-auto" data-id="element-248">
                        <table className="w-full border-collapse" data-id="element-249">
                          <thead data-id="element-250">
                            <tr className="bg-gray-50" data-id="element-251">
                              <th className="p-3 text-left font-medium text-gray-700 border" data-id="element-252">
                                Feature
                              </th>
                              {selectedProjectsData.map(project => <th key={project.id} className="p-3 text-center font-medium text-gray-700 border" data-id="element-253">
                                  {project.title}
                                </th>)}
                            </tr>
                          </thead>
                          <tbody data-id="element-254">
                            {[{
                        name: 'Sustainable Practices',
                        feature: 'sustainable'
                      }, {
                        name: 'Organic Certification',
                        feature: 'organic'
                      }, {
                        name: 'Technology Integration',
                        feature: 'technology'
                      }, {
                        name: 'Export Potential',
                        feature: 'export'
                      }, {
                        name: 'Community Impact',
                        feature: 'community'
                      }].map((feature, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-255">
                                <td className="p-3 border font-medium" data-id="element-256">
                                  {feature.name}
                                </td>
                                {selectedProjectsData.map(project => <td key={project.id} className="p-3 border text-center" data-id="element-257">
                                    {project.tags.map(tag => tag.toLowerCase()).includes(feature.feature) ? <CheckIcon size={18} className="text-green-500 mx-auto" data-id="element-258" /> : <XCircleIcon size={18} className="text-gray-300 mx-auto" data-id="element-259" />}
                                  </td>)}
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div> : <div className="p-8 text-center" data-id="element-260">
              <BarChart2Icon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-261" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-262">
                Select projects to compare
              </h3>
              <p className="text-gray-500" data-id="element-263">
                Choose up to 3 projects to see a detailed comparison
              </p>
            </div>}
        </div>
        {/* Footer */}
        <div className="p-4 border-t sticky bottom-0 bg-white" data-id="element-264">
          <div className="flex justify-end gap-3" data-id="element-265">
            <button onClick={onClose} className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" data-id="element-266">
              Close
            </button>
            {selectedProjectsData.length > 0 && <button className="btn btn-primary" data-id="element-267">View Detailed Report</button>}
          </div>
        </div>
      </motion.div>
    </motion.div>;
};